/* eslint-disable complexity */
import { skipToken } from '@reduxjs/toolkit/query/react';
import { BenefitTypes } from '@strata/constants';
import { formatDate } from '@strata/helper-functions';
import { TreatmentRequestsIcon } from '@strata/react-chat';
import { FormSectionWithRows } from '@strata/react-form-section';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation, useNavigate } from 'react-router';

import { Permissions } from 'services/constants';
import { type BackToDestination } from 'services/navigationHelpers';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useGetShippingInfoQuery } from 'store/api/onboardingApi';
import { useGetRequestsForTreatmentQuery } from 'store/api/payerRequestApi';
import { useGetNeedsByDateQuery } from 'store/api/performanceApi';
import { useGetPlaceOfServiceMapQuery } from 'store/api/referenceApi';
import { type ReferralDetails as ReferralDetailsInterface } from 'store/api/treatmentApi';

interface ReferralDetailsProps {
    referralDetails?: ReferralDetailsInterface;
    currentLocation: BackToDestination;
}

export const ReferralDetails = ({
    referralDetails = undefined,
    currentLocation,
}: ReferralDetailsProps) => {
    const { showProductTypeForPayer } = useFlags<{
        showProductTypeForPayer: boolean;
    }>();
    const { payerId, permissions } = useAppSelector((state) => state.auth);
    const pbmOnlyAccess = permissions.includes(Permissions.WRITE_PAYER_REQUEST_PBM_ONLY);
    const canSeeMemberEscalations = permissions.includes(Permissions.READ_PAYER_REQUEST_ESCALATION);
    const navigate = useNavigate();
    const location = useLocation();

    const { data: placeOfServiceMap } = useGetPlaceOfServiceMapQuery();
    const { data: treatmentRequests } = useGetRequestsForTreatmentQuery(
        payerId && referralDetails && canSeeMemberEscalations
            ? { payerId, treatmentId: referralDetails.treatmentId }
            : skipToken,
    );
    const { data: needsByDate } = useGetNeedsByDateQuery(
        referralDetails?.benefit === BenefitTypes.PHARMACY
            ? { treatmentId: referralDetails.treatmentId }
            : skipToken,
    );
    const { data: shippingInfo } = useGetShippingInfoQuery(
        referralDetails?.referralId
            ? {
                  referralId: referralDetails.referralId,
              }
            : skipToken,
    );

    const detailLabelValuePairs = [
        {
            label: 'Referral ID',
            value: referralDetails?.friendlyId,
        },
        {
            label: 'Place Of Service',
            value: placeOfServiceMap?.[referralDetails?.placeOfService ?? ''],
        },
        {
            label: 'Next Fill Needs By Date',
            value: formatDate(needsByDate?.needsByDate),
        },
        {
            label: 'Outreach Start Date',
            value: formatDate(referralDetails?.outreachDate),
        },
        {
            label: 'Servicing Start Date',
            value: formatDate(referralDetails?.referralStart),
        },
        {
            label: 'Referral Expiration',
            value: formatDate(referralDetails?.referralExpiration),
        },
        ...(showProductTypeForPayer
            ? [
                  {
                      label: 'Product Type',
                      value: referralDetails?.isBrand ? 'Brand' : 'Generic',
                  },
              ]
            : []),
        {
            label: 'Last Tracking Number',
            value: shippingInfo?.tracking_number,
        },
        {
            label: 'Shipment Date',
            value: formatDate(shippingInfo?.shipment_date),
        },
        {
            label: 'Shipment Carrier',
            value: shippingInfo?.shipment_carrier,
        },
        ...(canSeeMemberEscalations && (treatmentRequests ?? []).length > 0
            ? [
                  {
                      label: 'Payer Requests',
                      value: (
                          <TreatmentRequestsIcon
                              requests={treatmentRequests}
                              pbmOnlyAccess={pbmOnlyAccess}
                              onClick={(requestId: string) =>
                                  navigate(
                                      {
                                          pathname: AppRoutes.requestDetails(requestId),
                                      },
                                      {
                                          state: {
                                              backToDestination: {
                                                  ...currentLocation,
                                                  route: location.pathname,
                                              },
                                          },
                                      },
                                  )
                              }
                          />
                      ),
                  },
              ]
            : []),
    ];
    const pairsToShow = detailLabelValuePairs.map(({ label, value }, index) => (
        <DetailLabelValuePair
            key={label}
            style={{ width: (index + 1) % 3 ? '33%' : '34%' }}
            label={label}
            value={value}
        />
    ));
    const getThreePairs = (row: number) => {
        const zeroBasedIndex = row - 1;
        const rowStartIndex = zeroBasedIndex * 3;
        const rowEndIndex = row * 3;
        // If first index in row exists, then return that row
        if (pairsToShow[rowStartIndex]) {
            return pairsToShow.slice(rowStartIndex, rowEndIndex);
        }
        // Else, nothing to show for that row
        return null;
    };

    return (
        <FormSectionWithRows
            sectionTitle="Referral Details"
            isLoading={!referralDetails}
            rowOneContent={getThreePairs(1)}
            rowTwoContent={getThreePairs(2)}
            rowThreeContent={getThreePairs(3)}
            rowFourContent={getThreePairs(4)}
            rowFiveContent={getThreePairs(5)}
            rowSixContent={getThreePairs(6)}
        />
    );
};
